// Inquiry — a guest's booking inquiry / request-to-book, surfaced to the host
// to act on. Backed by gotidy's dedicated `inquiries` resource:
//
//   GET  /api/v1/customer/inquiries
//   POST /api/v1/customer/inquiries/:id/approve
//   POST /api/v1/customer/inquiries/:id/preapprove
//   POST /api/v1/customer/inquiries/:id/decline
//   POST /api/v1/customer/inquiries/:id/send-special-offer  (no UI yet)
//
// Channel notes:
// - Airbnb "accept" is a PRE-APPROVAL: the guest gets a 24h window to book at
//   the inquiry's dates/price. Nothing is confirmed or charged.
// - Decline/special-offer send a platform-specific `data` payload validated by
//   the backend. Airbnb decline: { decline_reason, message_to_guest,
//   message_to_platform } — some reasons require a min-30-char platform note
//   (never guest-facing). Decline reasons come from
//   GET /inquiries/:platform/decline-reasons.
// - Channel inquiries may carry no money data, so pricing is optional.
// - `available_actions` is only populated while the inquiry is still open
//   (status 'pending') on a platform with capture-side actions (Airbnb today).
// - Direct-booking ("tidy" platform) inquiries carry a saved card: "accept" is
//   POST /inquiries/:id/approve — ONE call that re-checks availability, creates
//   the guest reservation, and charges the card for stay + security deposit.
//   Their `amounts_breakdown` is TYPED ({type, value}) unlike channels'
//   ({description, amount}), and a `pending_requirements.card` hash means the
//   host approved but the charge was refused (approve stays available as retry).

// Lifecycle status, mirroring the backend model's STATUS_VALUES.
// 'responded': the host replied on the channel (or via TIDY) but did not
// pre-approve or decline; still actionable and booking-convertible.
export type InquiryStatus = 'pending' | 'responded' | 'preapproved' | 'declined' | 'expired' | 'approved';

export type InquirySource =
  | 'direct_booking'
  | 'airbnb'
  | 'vrbo'
  | 'booking_com'
  | 'other';

export type InquiryAction = 'preapprove' | 'decline' | 'special_offer' | 'approve';

export interface InquiryGuest {
  name: string;
  email?: string;
  phone?: string;
}

// pending_requirements.card — set when the host approved a tidy inquiry but the
// guest's card was REFUSED. Everything was rolled back, the inquiry stays
// pending (+24h expiry reset), the guest was emailed a link to fix the card,
// and 'approve' remains available as a retry.
export interface InquiryCardRequirement {
  reason: string; // card_declined | card_expired | wrong_card_number | insufficient_funds | zip_code_invalid | payment_failed
  failed_at: string; // ISO 8601
}

export interface InquiryPendingRequirements {
  card?: InquiryCardRequirement;
}

// Human labels for the closed card-failure reason vocabulary.
const CARD_FAILURE_REASON_LABELS: Record<string, string> = {
  card_declined: 'card declined',
  card_expired: 'card expired',
  wrong_card_number: 'wrong card number',
  insufficient_funds: 'insufficient funds',
  zip_code_invalid: 'ZIP code invalid',
  payment_failed: 'payment failed',
};

export function cardFailureReasonLabel(reason: string | undefined): string {
  return CARD_FAILURE_REASON_LABELS[reason ?? ''] ?? 'payment failed';
}

// Human copy for a 422 from POST /inquiries/:id/approve (`data.reason`,
// closed vocabulary). Nothing was charged; decline is the only action left.
const APPROVE_BLOCKED_REASON_MESSAGES: Record<string, string> = {
  dates_unavailable: 'These dates are no longer available.',
  min_stay_not_met: 'This stay no longer meets the minimum-stay requirement.',
  night_unpriced: 'Some of these nights no longer have a price set.',
};

export function approveBlockedMessage(
  reason: string | undefined,
  minStay?: number
): string {
  const detail =
    reason === 'min_stay_not_met' && minStay
      ? `This stay no longer meets the ${minStay}-night minimum stay.`
      : APPROVE_BLOCKED_REASON_MESSAGES[reason ?? ''] ??
        'These dates can no longer be booked.';
  return `${detail} You can decline the inquiry.`;
}

// What an inquiry-action error looks like by the time it reaches a page.
//
// ErrorInterceptor.formatError DISCARDS the HttpErrorResponse (so `status`
// never reaches component code) and re-throws a flattened
//   { code, message, err }
// where `err` is the raw backend body { error: { message, code, error, data } }
// and the backend's `code` mirrors the HTTP status (402/403/422). When the
// response has no wrapped body (network failure, non-API error) the
// interceptor passes the original object through instead, so `status` is kept
// as a fallback and every field stays optional.
export interface InquiryActionError {
  /** HTTP-status-mirroring backend code, e.g. 402 | 403 | 422. */
  code?: number;
  /** The backend's user-facing message, when one was sent. */
  message?: string;
  /** Closed-vocabulary detail, e.g. card_declined or min_stay_not_met. */
  reason?: string;
  /** 422 min_stay_not_met only: the minimum number of nights. */
  minStay?: number;
}

export function parseInquiryActionError(err: unknown): InquiryActionError {
  const flattened = err as {
    code?: unknown;
    status?: unknown;
    err?: { error?: { message?: unknown; data?: { reason?: unknown; min_stay?: unknown } } };
  };
  const body = flattened?.err?.error;
  const code =
    typeof flattened?.code === 'number'
      ? flattened.code
      : typeof flattened?.status === 'number'
        ? flattened.status
        : undefined;
  return {
    code,
    message: typeof body?.message === 'string' ? body.message : undefined,
    reason: typeof body?.data?.reason === 'string' ? body.data.reason : undefined,
    minStay: typeof body?.data?.min_stay === 'number' ? body.data.min_stay : undefined,
  };
}

// A single line in the host-payout breakdown, e.g.
//   { description: '1 night x R$520.00', amount_cents: 52000 }
//   { description: 'Service fee (host)', amount_cents: -9471 }  (fees are negative)
export interface InquiryAmount {
  description: string;
  amount_cents: number;
}

export interface InquiryPricing {
  currency: string; // ISO 4217, e.g. 'USD'
  nightly_rate_cents?: number;
  // null when the stay length is unknown — see the check_in_date note below.
  // Never fall back to 1: a guessed night count silently misprices the stay.
  nights: number | null;
  subtotal_cents?: number; // tidy: the nights_total_amount line
  security_deposit_cents?: number; // tidy: the security_deposit_amount line
  total_cents: number; // host payout — the "Total" (breakdown items sum to this)
  breakdown: InquiryAmount[]; // line items to render above the Total
}

export interface Inquiry {
  id: number; // Inquiry id — path id for the action endpoints
  status: InquiryStatus;
  source: InquirySource;
  platform: string; // raw backend platform slug ('airbnb', 'tidy', …)
  source_name: string; // display label, e.g. 'Airbnb'
  address_id: number;
  property_name: string;
  property_location?: string; // "Irvine, CA"
  guest: InquiryGuest;
  // YYYY-MM-DD, or null when the channel captured no firm dates. Airbnb threads
  // can start as a plain question and only later gain a dated booking inquiry,
  // and the backend's columns are nullable by design — so null is a normal,
  // expected state, NOT an error. Never coerce it to '' : Luxon renders an empty
  // string as the literal text "Invalid DateTime".
  check_in_date: string | null;
  check_out_date: string | null;
  total_guests?: number;
  message?: string;
  pricing?: InquiryPricing; // absent on channel inquiries (no money data captured)
  platform_url?: string; // host-facing deep link into the channel inbox — never guest-facing
  available_actions: InquiryAction[];
  decline_reasons: string[]; // platform decline-reason enum for this row
  // Tidy only: the exact amount /approve charges (stay + security deposit), cents.
  total_amount_cents?: number;
  // Tidy only: set when a prior approve's charge was refused (see the type doc).
  pending_requirements: InquiryPendingRequirements;
  // Direct-booking only: the guest saved a card the host charges on accept.
  card_on_file: boolean;
  card_brand?: string; // 'Visa'
  card_last4?: string; // '4242'
  expires_at?: string; // ISO 8601 — pre-approval hold window, when known
  uuid: string;
  created_by: 'customer' | 'guest';
  customer_note?: string;
  guest_reservation_id?: number;
}

// Raw row from GET /api/v1/customer/inquiries. `address_id` is always at the
// root; the full `address` hash is only present with ?includes=address.
export interface InquiryRow {
  id: number;
  // The guest status page's key (book.tidy.com/reservation/<uuid>) — the only
  // way to that page outside the guest's emailed link.
  uuid: string;
  status: InquiryStatus;
  platform: string; // 'airbnb'
  // Both null on tidy rows — there is no external channel thread to link to.
  platform_conversation_id: string | null;
  platform_inquiry_url: string | null;
  guest_name: string | null;
  address_id: number;
  address?: { id: number; address1: string; address2: string | null; city: string | null };
  check_in_date: string | null; // YYYY-MM-DD
  check_out_date: string | null; // YYYY-MM-DD
  expires_at: string | null; // ISO 8601, when the inquiry has a pending pre-approval
  // ISO 8601: when the channel expects the host's answer by (Airbnb: ask + 24h).
  // Only while 'pending'; null once answered, resolved, or on tidy rows.
  response_deadline?: string | null;
  message?: string | null; // the guest's note / special request, when the channel captures one
  // Guest counts, flattened to the root (parity with the reservation payload).
  total_adults: number | null;
  total_children: number | null;
  total_infants: number | null;
  total_pets: number | null;
  total_guests: number | null;
  // Money, integer cents. Only the currency is sent; the host payout / Total is
  // the SUM of amounts_breakdown (channels) or `total_amount` (tidy).
  currency: string | null;
  // Line items, integer cents. Channels: { description, amount } (negatives are
  // fees; sum = host payout). Tidy: TYPED { type, value } items where type is
  // 'nights_total_amount' | 'security_deposit_amount'.
  amounts_breakdown: Array<{
    description?: string;
    amount?: number;
    type?: string;
    value?: number;
  }>;
  available_actions: InquiryAction[];
  created_by?: 'customer' | 'guest';
  customer_note?: string | null;
  guest_reservation?: { id: number; state: string } | null;
  // Tidy only: the amount /approve charges (stay + deposit); null on channel rows.
  total_amount?: number | null;
  // Tidy only: `card` key present = host approved but the charge was refused.
  pending_requirements?: { card?: { reason: string; failed_at: string } } | null;
  // Tidy only: full guest contact (channels only ever send `guest_name`).
  guest?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  // Opt-in via ?includes=card (tidy only): the saved card charged on approve.
  card?: { last4: string } | null;
  // An inquiry only has a booker name (`guest_name`) — never a guest list.
}

// The `inquiry`-typed message a guest room's thread returns
// (GET /customer/shared-inbox/messages): built at read time from the inquiries
// table — no chat-service id — with the raw inquiry row as its content.
export interface InquiryMessage {
  id: null;
  type: 'inquiry';
  content: { inquiry: InquiryRow };
  sent_at: string;
  is_read: boolean;
  is_internal: boolean;
  from_member: unknown;
  was_sent_by: string;
  chat_room_key?: string;
  metadata?: { inquiry_id?: number };
}
